import { memo, useCallback, useState } from 'react';

import { faCalendar, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { TranscriptWithCount } from '../../../../api/types/backend-dto-types';
import { useDeleteTranscript } from '@/api/mutations/useDeleteTranscript';
import Button from '@/components/atoms/Button';
import { ConfirmationModal } from '@/components/molecules/Modals/Confirmation';
import formatDate from '@/helpers/formatDate';

interface TranscriptListItemProps {
  transcript: TranscriptWithCount;
  onPlay: (transcriptId: string) => void;
  caseId: string;
}

export const TranscriptListItem = memo(({ transcript, onPlay, caseId }: TranscriptListItemProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { mutateAsync: deleteTranscript, isLoading } = useDeleteTranscript(caseId);

  const handlePlay = useCallback(() => {
    onPlay(transcript.id);
  }, [transcript.id, onPlay]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setShowDeleteModal(false);
    await deleteTranscript(transcript.id);
  }, [deleteTranscript, transcript.id]);

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Unknown';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="group relative mb-3 flex w-full flex-col rounded-lg border bg-white p-4 shadow-sm transition-all hover:border-gray-300 hover:shadow-md">
      <div className="flex w-full flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-gray-900">{transcript.name}</div>

            <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <FontAwesomeIcon icon={faCalendar} className="h-3 w-3" />
                <span>{formatDate(transcript.createdDate)}</span>
                {transcript.durationSeconds && (
                  <span className="ml-1">• {formatDuration(transcript.durationSeconds)}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-dashed pt-4">
          <div className="flex items-center gap-2">
            <Button text="Open" type="primary" size="xs" rounded="md" onClick={handlePlay} disabled={isLoading} />
            <Button
              text={isLoading ? 'Deleting...' : 'Delete'}
              type="delete"
              size="xs"
              rounded="md"
              onClick={handleDeleteClick}
              loading={isLoading}
            />
          </div>

          <div className="flex items-center gap-3 text-xs text-gray-500">
            {transcript.inconsistencyCount !== undefined && (
              <span>{transcript.inconsistencyCount} inconsistencies</span>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        title="Delete Transcript"
        description={`Are you sure you want to delete "${transcript.name}"? This will permanently delete all transcript data, audio files, and inconsistencies. This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        isLoading={false}
        confirmButtonText="Delete Transcript"
      />
    </div>
  );
});
